import type { User } from 'firebase/auth';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { UserProfile } from '@penticton-dev-events/shared';

import { useFirebaseServices } from './FirebaseProvider';
import {
  ensureUserProfile,
  updateUserRole,
  type CreateUserProfilePayload,
} from '../../features/auth/api/userRepository';
import {
  signUpWithEmail,
  signInWithEmail,
  sendPasswordReset,
  type SignUpWithEmailPayload,
  type SignInWithEmailPayload,
} from '../../features/auth/api/emailAuthRepository';

export type AuthContextValue = {
  readonly user: User | null;
  readonly profile: UserProfile | null;
  readonly loading: boolean;
  readonly signIn: () => Promise<void>;
  readonly signInWithEmail: (payload: SignInWithEmailPayload) => Promise<void>;
  readonly signUpWithEmail: (payload: SignUpWithEmailPayload) => Promise<void>;
  readonly resetPassword: (email: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly promoteToOrganizer: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

const googleProvider = new GoogleAuthProvider();

type AuthProviderProps = {
  readonly children: ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { auth, firestore } = useFirebaseServices();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, nextUser => {
      void (async () => {
        setLoading(true);
        setUser(nextUser);

        if (!nextUser) {
          setProfile(null);
          setLoading(false);
          return;
        }

        try {
          const payload: CreateUserProfilePayload = {
            uid: nextUser.uid,
            displayName: nextUser.displayName,
            email: nextUser.email,
            photoURL: nextUser.photoURL,
          };
          const userProfile = await ensureUserProfile(firestore, payload);

          // Check if user is suspended
          if (userProfile.accountStatus === 'suspended') {
            console.log('Suspended user attempted to sign in - signing out');
            await firebaseSignOut(auth);
            setProfile(null);
            setUser(null);
            setLoading(false);
            return;
          }

          setProfile(userProfile);
        } catch (error) {
          // Log error but don't throw - keep user authenticated even if profile creation fails
          console.error('Failed to load user profile:', error);
          setProfile(null);
        } finally {
          setLoading(false);
        }
      })();
    });

    return () => unsubscribe();
  }, [auth, firestore]);

  const signIn = async () => {
    const result = await signInWithPopup(auth, googleProvider);

    // Check if user is suspended immediately after sign-in
    const payload: CreateUserProfilePayload = {
      uid: result.user.uid,
      displayName: result.user.displayName,
      email: result.user.email,
      photoURL: result.user.photoURL,
    };
    const userProfile = await ensureUserProfile(firestore, payload);

    // Check if user is suspended
    if (userProfile.accountStatus === 'suspended') {
      await firebaseSignOut(auth);
      throw new Error('Your account has been suspended. Please contact support for more information.');
    }
  };

  const handleSignInWithEmail = async (payload: SignInWithEmailPayload) => {
    const result = await signInWithEmail(auth, payload);

    // Check if user is suspended immediately after sign-in
    const profilePayload: CreateUserProfilePayload = {
      uid: result.user.uid,
      displayName: result.user.displayName,
      email: result.user.email,
      photoURL: result.user.photoURL,
    };
    const userProfile = await ensureUserProfile(firestore, profilePayload);

    // Check if user is suspended
    if (userProfile.accountStatus === 'suspended') {
      await firebaseSignOut(auth);
      throw new Error('Your account has been suspended. Please contact support for more information.');
    }
  };

  const handleSignUpWithEmail = async (payload: SignUpWithEmailPayload) => {
    const userCredential = await signUpWithEmail(auth, payload);

    // After signup, ensure the Firestore profile has the correct display name
    // This handles race condition where onAuthStateChanged fires before updateProfile completes
    if (userCredential.user) {
      const profilePayload: CreateUserProfilePayload = {
        uid: userCredential.user.uid,
        displayName: payload.displayName, // Use the display name from signup form
        email: userCredential.user.email,
        photoURL: userCredential.user.photoURL,
      };
      const userProfile = await ensureUserProfile(firestore, profilePayload);
      setProfile(userProfile);
    }
  };

  const resetPassword = async (email: string) => {
    await sendPasswordReset(auth, email);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const promoteToOrganizer = async () => {
    if (!user) {
      throw new Error('User must be signed in to update role');
    }

    await updateUserRole(firestore, user.uid, 'organizer');
    setProfile(current => (current ? { ...current, role: 'organizer' } : current));
  };

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    signIn,
    signInWithEmail: handleSignInWithEmail,
    signUpWithEmail: handleSignUpWithEmail,
    resetPassword,
    signOut,
    promoteToOrganizer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export default AuthProvider;
